<script setup lang="ts">
import { VList } from "virtua/vue";
import { computed, ref } from "vue";

import { useMetadataStore } from "@/stores/metadata-store";

const selectedFileReferences = defineModel<string[]>({ required: true });

const scope = ref<"filtered" | "all">("filtered");
const searchText = ref("");

const filteredFilesCount = computed(() => useMetadataStore().filteredFiles.length);
const allFilesCount = computed(() => useMetadataStore().requestedFiles.length);

const scopedFiles = computed(() =>
  scope.value === "filtered" ? useMetadataStore().filteredFiles : useMetadataStore().requestedFiles,
);

const visibleFiles = computed(() => {
  const search = searchText.value.trim().toLowerCase();
  return scopedFiles.value.filter((file) => search === "" || file.displayName?.toLowerCase().includes(search));
});

const selectedSet = computed(() => new Set(selectedFileReferences.value));

const allVisibleSelected = computed(
  () => visibleFiles.value.length > 0 && visibleFiles.value.every((file) => selectedSet.value.has(file.fileReference)),
);

function toggleFile(fileReference: string, checked: boolean) {
  selectedFileReferences.value = checked
    ? [...selectedFileReferences.value, fileReference]
    : selectedFileReferences.value.filter((ref) => ref !== fileReference);
}

function toggleAllVisible(checked: boolean) {
  const visibleReferences = new Set(visibleFiles.value.map((file) => file.fileReference));

  selectedFileReferences.value = checked
    ? [...new Set([...selectedFileReferences.value, ...visibleReferences])]
    : selectedFileReferences.value.filter((ref) => !visibleReferences.has(ref));
}
</script>
<template>
  <div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-2">
    <fieldset class="tw-flex tw-items-center tw-gap-4 tw-text-sm">
      <legend class="tw-mb-1 tw-text-xs">File List</legend>
      <label class="tw-flex tw-cursor-pointer tw-items-center tw-gap-1">
        <input v-model="scope" type="radio" name="migration-file-scope" value="filtered" />
        Filtered Files ({{ filteredFilesCount }})
      </label>
      <label class="tw-flex tw-cursor-pointer tw-items-center tw-gap-1">
        <input v-model="scope" type="radio" name="migration-file-scope" value="all" />
        All Files ({{ allFilesCount }})
      </label>
    </fieldset>
    <div class="tw-flex tw-items-center tw-gap-2">
      <label for="migration-file-search" class="tw-sr-only">Search files by name</label>
      <input
        id="migration-file-search"
        v-model.trim="searchText"
        type="search"
        placeholder="Search files by name..."
        class="tw-w-full tw-rounded tw-border tw-border-gray-500 tw-p-2 tw-text-sm"
      />
    </div>
    <div class="tw-flex tw-items-center tw-justify-between tw-text-xs">
      <label class="tw-flex tw-cursor-pointer tw-items-center tw-gap-1">
        <input
          type="checkbox"
          :checked="allVisibleSelected"
          :disabled="visibleFiles.length === 0"
          @change="toggleAllVisible(($event.target as HTMLInputElement).checked)"
        />
        Select All Shown
      </label>
      <p role="status">{{ selectedFileReferences.length }} file(s) selected</p>
    </div>
    <div class="tw-min-h-0 tw-flex-1 tw-rounded tw-border tw-border-gray-300">
      <p v-if="visibleFiles.length === 0" class="tw-p-3 tw-text-center tw-text-sm tw-text-gray-600">
        No files match the current scope/search.
      </p>
      <VList v-else v-slot="{ item }" class="tw-size-full tw-min-h-20" :data="visibleFiles" :item-size="32">
        <label
          :key="item.fileReference"
          class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-truncate tw-px-2 tw-py-1 tw-text-sm hover:tw-bg-gray-100"
        >
          <input
            type="checkbox"
            :checked="selectedSet.has(item.fileReference)"
            @change="toggleFile(item.fileReference, ($event.target as HTMLInputElement).checked)"
          />
          <span class="tw-truncate">{{ item.displayName || "[UNKNOWN_FILE_NAME]" }}</span>
        </label>
      </VList>
    </div>
  </div>
</template>
